// « Flux en retard » — the freshness gate's banner (PMS-LEDGER-L0-BRIEF §4).
//
// Shown to the operator and to the desk **identically** (operator, 2026-10-04):
// a stale feed must read as « the data is late », never as « nothing found ».
// A gate that is open renders nothing. A gate that cannot be read renders a
// banner too, because a failed read must not look like « all clear ».
//
// The terminal feed sees the first terminal only (the backup terminal, rang
// 501, appears on the SG statement alone — brief §6 P3), so every closed
// banner says so.

import { useEffect, useState } from 'react';
import type { IsoDate, LedgerDayGate, LedgerGateReason } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope } from './PmsProvider.js';

const REASON_FR: Record<LedgerGateReason, string> = {
  terminal_never_polled: 'Le flux du terminal n’a jamais été relevé.',
  terminal_last_poll_failed: 'Le dernier relevé du terminal a échoué.',
  terminal_stale: 'Le flux du terminal n’a pas encore relevé les dernières remises de ce jour.',
  mirror_not_covered: 'Le miroir Vega ne couvre pas encore ce jour.',
  vega_not_closed: 'Vega n’a pas encore clos ce jour.',
};

const COVERAGE_FR = 'Le flux ne couvre que le terminal principal (le second terminal n’apparaît qu’au relevé de la banque).';

const at = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', {
        timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : 'jamais';

/** What to show for a gate answer: nothing when open, else the first failing
 *  condition with the times that explain it. Pure, so it is tested alone. */
export function gateMessage(gate: LedgerDayGate): string[] | null {
  if (gate.open && gate.reason === null) return null;
  const lines = [gate.reason ? REASON_FR[gate.reason] : 'Les flux ne sont pas à jour pour ce jour.'];
  lines.push(`Dernier relevé terminal : ${at(gate.terminalLastPollAt)} · miroir Vega : ${at(gate.mirrorCoveredThrough)}.`);
  lines.push(COVERAGE_FR);
  return lines;
}

export function LedgerGateNotice({ lines }: { lines: string[] | null }) {
  if (!lines) return null;
  return (
    <div className="pms-warn" role="status" data-testid="ledger-gate-banner">
      <strong>Flux en retard — aucune anomalie n’est affichée pour ce jour.</strong>
      {lines.map((l) => (
        <p key={l} className="muted">{l}</p>
      ))}
    </div>
  );
}

export function LedgerGateBanner({ day }: { day: IsoDate }) {
  const { ledgerGate } = usePms();
  const { current } = usePmsScope();
  const allowed = useCapability(CAPABILITIES.FIN_FEED_GATE);
  const [lines, setLines] = useState<string[] | null>(null);

  useEffect(() => {
    if (!current || !allowed) return;
    let cancelled = false;
    void (async () => {
      const res = await ledgerGate.day(current, day);
      if (cancelled) return;
      if (!res.ok) {
        setLines(['L’état des flux est illisible : ne pas conclure à l’absence d’anomalie.']);
        return;
      }
      setLines(gateMessage(res.value));
    })();
    return () => {
      cancelled = true;
    };
  }, [ledgerGate, current, allowed, day]);

  return <LedgerGateNotice lines={lines} />;
}
